import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDownIcon, ChevronUpIcon, MessageSquareIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { ComparisonTable } from '../components/pricing/ComparisonTable';
import { ECommerceComparisonTable } from '../components/pricing/ECommerceComparisonTable';
import { WebsiteCareComparisonTable } from '../components/pricing/WebsiteCareComparisonTable';
export function Pricing() {
  const [activeTab, setActiveTab] = useState<'web-design' | 'ecommerce' | 'website-care'>('web-design');
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBronzeClick = () => {
    window.open('https://buy.stripe.com/aFa14hfmQ5ga8v85gsaZi00', '_blank');
  };
  const handleSilverClick = () => {
    window.open('https://buy.stripe.com/eVqcMZ3E8cICbHk4coaZi01', '_blank');
  };
  const handleGoldClick = () => {
    window.open('https://buy.stripe.com/eVq3cpfmQ6ke8v86kwaZi02', '_blank');
  };
  const handleESilverClick = () => {
    window.open('https://buy.stripe.com/bJeeV7b6AgYSfXA7oAaZi03', '_blank');
  };
  const handleEGoldClick = () => {
    window.open('https://buy.stripe.com/4gMaER0rWcICfXA6kwaZi04', '_blank');
  };
  const handleEPlatinumClick = () => {
    window.open('https://buy.stripe.com/eVqbIVcaE9wq9zc8sEaZi05', '_blank');
  };
  const handleEssentialCareClick = () => {
    window.open('https://buy.stripe.com/14A8wJ1w0cICfXA10caZi06', '_blank');
  };
  const handleTotalGrowthClick = () => {
    window.open('https://buy.stripe.com/9B628l7UobEy9zcfV6aZi07', '_blank');
  };
  const handleOneOffFixClick = () => {
    window.open('https://buy.stripe.com/28EdR3fmQ8smaDgbEQaZi08', '_blank');
  };
  return <>
      <Helmet data-id="element-1592">
        <title data-id="element-1593">
          Pricing | Web Design, E-Commerce & Maintenance Packages (Inc VAT)
        </title>
        <meta name="description" content="Simple, transparent pricing for every business. Compare plans and choose your package." data-id="element-1594" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-1595">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1596">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1597">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-1598">
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-white/90 mb-8" data-id="element-1599">
              Choose from our range of web design, e-commerce, and maintenance
              packages. All prices include VAT with no hidden fees or surprises.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Tab Navigation */}
      <section className="py-8 bg-white dark:bg-[#1E1E1E] border-b border-gray-200 dark:border-gray-800" data-id="element-1600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1601">
          <div className="max-w-3xl mx-auto" data-id="element-1602">
            <div className="flex flex-wrap justify-center gap-2 md:gap-4" data-id="element-1603">
              <button onClick={() => setActiveTab('web-design')} className={`px-6 py-3 rounded-full text-base font-medium transition-colors ${activeTab === 'web-design' ? 'bg-[#0DC3DC] text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1604">
                Web Design
              </button>
              <button onClick={() => setActiveTab('ecommerce')} className={`px-6 py-3 rounded-full text-base font-medium transition-colors ${activeTab === 'ecommerce' ? 'bg-[#0DC3DC] text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1605">
                E-Commerce
              </button>
              <button onClick={() => setActiveTab('website-care')} className={`px-6 py-3 rounded-full text-base font-medium transition-colors ${activeTab === 'website-care' ? 'bg-[#0DC3DC] text-white shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1606">
                Maintenance Packages
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1607">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1608">
          <div className="max-w-7xl mx-auto" data-id="element-1609">
            <AnimatePresence mode="wait" data-id="element-1610">
              {activeTab === 'web-design' && <motion.div key="web-design" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1611">
                  <PricingCard title="Bronze" subtitle="Starter" price="£399" popular={false} features={['Up to 5 pages', 'Responsive design', 'Enquiry form', 'Blog option', 'Basic SEO', '1 business email']} iconSrc="https://images.unsplash.com/photo-1605478328994-f93e98217da7?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Bronze web design plan icon" ctaText="Get Started" onClick={handleBronzeClick} data-id="element-1612" />
                  <PricingCard title="Silver" subtitle="Growing Business" price="£599" popular={true} features={['Up to 10 pages', 'Branding + favicon', '3 business emails', 'SEO setup', 'Domain + SSL (year 1)', '30-day support']} iconSrc="https://images.unsplash.com/photo-1568480289356-5a75d0fd47fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Silver web design plan icon" ctaText="Most Popular" onClick={handleSilverClick} data-id="element-1613" />
                  <PricingCard title="Gold" subtitle="Full Service" price="£899" popular={false} features={['Up to 15 pages', 'Logo + trust badges', 'Advanced SEO', 'Payment links', 'Content writing included', 'GA + GSC setup']} iconSrc="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Gold web design plan icon" ctaText="Get Started" onClick={handleGoldClick} data-id="element-1614" />
                </motion.div>}
              {activeTab === 'ecommerce' && <motion.div key="ecommerce" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1615">
                  <PricingCard title="E-Silver" subtitle="Essential Store" price="£838.80" popular={false} features={['Unlimited products', 'PayPal/Stripe integration', 'Blog functionality', 'Basic product search', '3-month hosting included', '24/7 ticket support']} iconSrc="https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: E-Silver e-commerce plan badge" ctaText="Get Started" onClick={handleESilverClick} data-id="element-1616" />
                  <PricingCard title="E-Gold" subtitle="Growing Business" price="£1,195.20" popular={true} features={['All E-Silver features', 'Abandoned cart recovery', 'Newsletter integration', 'Wishlist functionality', 'Social login options', '1-to-1 training session']} iconSrc="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: E-Gold e-commerce plan badge" ctaText="Most Popular" onClick={handleEGoldClick} data-id="element-1617" />
                  <PricingCard title="E-Platinum" subtitle="Enterprise Solution" price="£1,796.40" popular={false} features={['All E-Gold features', 'Advanced analytics', 'Subscriptions & recurring', 'Gift cards functionality', 'Product bundles/upsells', 'Custom functionality']} iconSrc="https://images.unsplash.com/photo-1614028674026-a65e31bfd27c?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: E-Platinum e-commerce plan badge" ctaText="Get Started" onClick={handleEPlatinumClick} data-id="element-1618" />
                </motion.div>}
              {activeTab === 'website-care' && <motion.div key="website-care" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1619">
                  <PricingCard title="Essential Care" subtitle="Basic Maintenance" price="£79/m" popular={false} features={['Hosting & domain management', 'SSL renewal', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates', 'Basic email support']} iconSrc="https://images.unsplash.com/photo-1633613286991-611fe299c4be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Essential website care plan icon" ctaText="Get Started" onClick={handleEssentialCareClick} data-id="element-1620" />
                  <PricingCard title="Total Growth" subtitle="Complete Solution" price="£299/m" popular={true} features={['All Essential Care features', 'Priority support', 'Ongoing SEO', 'Performance tuning', 'Monthly analytics report', 'Content updates (1 hr/m)']} iconSrc="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Total Growth website care plan icon" ctaText="Most Popular" onClick={handleTotalGrowthClick} data-id="element-1621" />
                  <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5
              }} className="relative rounded-xl overflow-hidden border border-[#E6E6E6] dark:border-[#333333] shadow-md" data-id="element-1622">
                    <div className="p-6 bg-[#254055]" data-id="element-1623">
                      <div className="flex items-center mb-4" data-id="element-1624">
                        <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0" data-id="element-1625">
                          <img src="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" alt="ALT: One-off website fix plan icon" className="w-full h-full object-cover" data-id="element-1626" />
                        </div>
                        <div data-id="element-1627">
                          <h3 className="text-xl font-bold text-white" data-id="element-1628">
                            One-off Fix
                          </h3>
                          <p className="text-white/70 text-sm" data-id="element-1629">
                            Single Issue Resolution
                          </p>
                        </div>
                      </div>
                      <div className="mb-6" data-id="element-1630">
                        <span className="text-3xl font-bold text-[#0DC3DC]" data-id="element-1631">
                          £150
                        </span>
                        <span className="text-white/70 ml-1" data-id="element-1632">(Inc VAT)</span>
                      </div>
                      <ul className="space-y-3 mb-6" data-id="element-1633">
                        <li className="flex items-start" data-id="element-1634">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1635" />
                          <span className="text-white/90" data-id="element-1636">Updates</span>
                        </li>
                        <li className="flex items-start" data-id="element-1637">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1638" />
                          <span className="text-white/90" data-id="element-1639">Security check</span>
                        </li>
                        <li className="flex items-start" data-id="element-1640">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1641" />
                          <span className="text-white/90" data-id="element-1642">Backup</span>
                        </li>
                        <li className="flex items-start" data-id="element-1643">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1644" />
                          <span className="text-white/90" data-id="element-1645">
                            Performance tweaks
                          </span>
                        </li>
                        <li className="flex items-start" data-id="element-1646">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1647" />
                          <span className="text-white/90" data-id="element-1648">30-day support</span>
                        </li>
                        <li className="flex items-start" data-id="element-1649">
                          <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-1650" />
                          <span className="text-white/90" data-id="element-1651">
                            No ongoing commitment
                          </span>
                        </li>
                      </ul>
                      <button onClick={handleOneOffFixClick} className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 bg-white/10 hover:bg-white/20 text-white border border-white/20" data-id="element-1652">
                        Get Started
                      </button>
                    </div>
                  </motion.div>
                </motion.div>}
            </AnimatePresence>
            <div className="mt-12 text-center" data-id="element-1653">
              <button onClick={toggleTable} className="inline-flex items-center justify-center mx-auto px-8 py-3 rounded-full font-medium text-[15px] text-[#0DC3DC] bg-transparent border border-[#0DC3DC] border-opacity-40 hover:bg-gradient-to-r hover:from-[#0DC3DC] hover:to-[#00A8BF] hover:text-white hover:border-opacity-0 transition-all duration-300 shadow-sm hover:shadow-lg" style={{
              fontFamily: 'Poppins, sans-serif',
              minHeight: '46px'
            }} data-id="element-1654">
                <span className="mr-2" data-id="element-1655">
                  {tableExpanded ? 'Hide detailed comparison' : 'Show detailed comparison'}
                </span>
                {tableExpanded ? <ChevronUpIcon size={16} data-id="element-1656" /> : <ChevronDownIcon size={16} data-id="element-1657" />}
              </button>
            </div>
            {/* Comparison Tables */}
            {tableExpanded && <AnimatePresence data-id="element-1658">
                {activeTab === 'web-design' && <ComparisonTable data-id="element-1659" />}
                {activeTab === 'ecommerce' && <ECommerceComparisonTable data-id="element-1660" />}
                {activeTab === 'website-care' && <WebsiteCareComparisonTable data-id="element-1661" />}
              </AnimatePresence>}
          </div>
        </div>
      </section>
      {/* Trust Section */}
      <section className="py-12 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1662">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1663">
          <div className="max-w-4xl mx-auto" data-id="element-1664">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#212C34] p-8 rounded-xl shadow-md text-center" data-id="element-1665">
              <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-1666">
                Your Website, Your Control
              </h3>
              <p className="text-white/90 text-lg mb-6" data-id="element-1667">
                Full ownership of your site + 3 months free hosting + free
                domain & SSL
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1668">
                <div className="flex items-center justify-center" data-id="element-1669">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-1670" />
                  <span className="text-white/90" data-id="element-1671">100% Site Ownership</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-1672">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-1673" />
                  <span className="text-white/90" data-id="element-1674">3 Months Free Hosting</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-1675">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-1676" />
                  <span className="text-white/90" data-id="element-1677">Free Domain & SSL</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1678">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1679">
          <div className="max-w-3xl mx-auto" data-id="element-1680">
            <div className="text-center mb-12" data-id="element-1681">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl font-bold mb-4" data-id="element-1682">
                Common Questions About Our Pricing
              </motion.h2>
            </div>
            <div className="space-y-6" data-id="element-1683">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-lg shadow-sm" data-id="element-1684">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1685">
                  Are there any hidden fees?
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1686">
                  No, all our prices are transparent and include VAT. We'll
                  never charge you extra unless you request additional services
                  outside the scope of your chosen package.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-lg shadow-sm" data-id="element-1687">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1688">
                  Can I upgrade my plan later?
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1689">
                  Yes, you can upgrade your plan at any time. We'll simply
                  charge the difference between your current plan and the
                  upgraded one. Downgrading is also possible with 30 days'
                  notice.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-lg shadow-sm" data-id="element-1690">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1691">
                  Do you offer payment plans?
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1692">
                  Yes, we offer 0% interest payment plans over 3 monthly
                  installments for all our web design and e-commerce packages.
                  Care plans are billed monthly or annually (with a discount for
                  annual payment).
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-lg shadow-sm" data-id="element-1693">
                <h3 className="text-xl font-semibold mb-3" data-id="element-1694">
                  What if I need a custom solution?
                </h3>
                <p className="text-gray-600 dark:text-gray-300" data-id="element-1695">
                  We understand that every business is unique. If our standard
                  packages don't quite fit your needs, we're happy to create a
                  custom solution tailored specifically for you. Contact us for
                  a personalized quote.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1696">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1697">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1698">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl font-bold mb-4" data-id="element-1699">
              Ready to get started?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1700">
              Contact us today to discuss your project requirements. We'll help
              you choose the right plan and answer any questions you may have.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1701">
              <Button size="lg" onClick={scrollToContact} data-id="element-1702">
                Request a Quote
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Floating Quote Button */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 1
    }} className="fixed bottom-6 right-6 z-50" data-id="element-1703">
        <Button onClick={scrollToContact} className="rounded-full px-6 py-3 shadow-lg text-base" data-id="element-1704">
          <MessageSquareIcon size={18} className="mr-2" data-id="element-1705" />
          Request Quote
        </Button>
      </motion.div>
    </>;
}